import React, { useState, useEffect } from "react";
import { Button, Card, Typography, message, Modal, Spin, Alert, Tag } from "antd";
import { ExclamationCircleOutlined, ReloadOutlined, CheckCircleOutlined, MessageOutlined, ShoppingCartOutlined } from "@ant-design/icons";
import { getDemoStatus, resetDemoSandbox, getWebchatLink, getStoreConfig } from "../../../services/polly/demoService";

const { Title, Text, Paragraph } = Typography;

export default function Sandbox() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [openingChat, setOpeningChat] = useState(false);
  const [openingStore, setOpeningStore] = useState(false);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const data = await getDemoStatus();
      setStatus(data);
    } catch (err) {
      message.error("Failed to load sandbox status");
    }
    setLoading(false);
  };

  useEffect(() => { fetchStatus(); }, []);

  const handleReset = () => {
    Modal.confirm({
      title: "Reset Sandbox",
      icon: <ExclamationCircleOutlined />,
      content: "Reset this sandbox to its initial state? This clears all conversation history, journey enrollments, and research requests since the last reset. This cannot be undone.",
      okText: "Reset",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        setResetting(true);
        setLastResult(null);
        try {
          const result = await resetDemoSandbox();
          setLastResult(result);
          if (result.success) {
            message.success("Sandbox reset complete");
          } else {
            message.error("Reset completed with issues");
          }
          await fetchStatus();
        } catch (err) {
          message.error("Reset failed: " + (err.message || "Unknown error"));
          setLastResult({ success: false, warnings: [err.message || "Unknown error"] });
        }
        setResetting(false);
      },
    });
  };

  const handleOpenWebchat = async () => {
    setOpeningChat(true);
    try {
      const data = await getWebchatLink();
      const timothyBase = window.location.hostname === "localhost"
        ? `http://${data.subdomain}.localhost:3846`
        : `https://${data.subdomain}.chatstream.blustream.io`;
      window.open(`${timothyBase}?magiclink=${data.code}`, "_blank");
    } catch (err) {
      message.error("Failed to open webchat: " + (err.message || "Unknown error"));
    }
    setOpeningChat(false);
  };

  const handleOpenStore = async () => {
    setOpeningStore(true);
    try {
      const data = await getStoreConfig();
      const gandalfBase = window.location.hostname === "localhost"
        ? "http://localhost:9080"
        : "";  // deployed: same origin via proxy
      const params = new URLSearchParams({
        customerId: data.customerId,
        profileId: data.profileId,
        profileEmail: data.profileEmail,
        productName: data.productName,
        gandalfBase: gandalfBase
      });
      const storeUrl = gandalfBase + "/demo/sandbox-store.html?" + params.toString();
      window.open(storeUrl, "_blank");
    } catch (err) {
      message.error("Failed to open store: " + (err.message || "Unknown error"));
    }
    setOpeningStore(false);
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!status?.isDemoTenant) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="info" message="This tenant is not configured as a demo sandbox." />
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 720 }}>
      <Title level={3}>Sandbox Management</Title>

      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 12 }}>
          <Text strong>Integration Type:</Text>
          <Tag color="blue">{status.integrationType}</Tag>
        </div>
        <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
          <Text strong>Last Reset:</Text>
          <Text>{status.lastResetAt && status.lastResetAt.length > 1 ? new Date(status.lastResetAt.replace(/\[.*\]$/, '')).toLocaleString() : "Never"}</Text>
        </div>
      </Card>

      <Card>
        <Paragraph>
          Reset this sandbox to its initial state. All accumulated conversations, journey enrollments,
          research requests, and contact data will be cleared. The seed configuration (products, journeys,
          attribute definitions, integration mappings) will be preserved.
        </Paragraph>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <Button
            type="primary"
            icon={<MessageOutlined />}
            loading={openingChat}
            onClick={handleOpenWebchat}
            size="large"
          >
            Open Webchat
          </Button>
          <Button
            type="primary"
            icon={<ShoppingCartOutlined />}
            loading={openingStore}
            onClick={handleOpenStore}
            size="large"
          >
            Open Demo Store
          </Button>
          <Button
            type="primary"
            danger
            icon={<ReloadOutlined />}
            loading={resetting}
            onClick={handleReset}
            size="large"
          >
            {resetting ? "Resetting..." : "Reset Sandbox"}
          </Button>
        </div>
      </Card>

      {lastResult && (
        <Card style={{ marginTop: 16 }}>
          {lastResult.success ? (
            <Alert
              type="success"
              icon={<CheckCircleOutlined />}
              message="Reset Successful"
              description={`Reset completed at ${new Date(lastResult.resetAt).toLocaleString()}`}
              showIcon
            />
          ) : (
            <Alert
              type="error"
              message="Reset Failed"
              description={lastResult.warnings?.join("; ") || "Unknown error"}
              showIcon
            />
          )}

          {lastResult.notes?.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <Text strong>Notes:</Text>
              <ul style={{ marginTop: 4, paddingLeft: 20 }}>
                {lastResult.notes.map((note, i) => (
                  <li key={i}><Text type="secondary">{note}</Text></li>
                ))}
              </ul>
            </div>
          )}

          {lastResult.warnings?.length > 0 && lastResult.success && (
            <div style={{ marginTop: 12 }}>
              <Text strong>Warnings:</Text>
              <ul style={{ marginTop: 4, paddingLeft: 20 }}>
                {lastResult.warnings.map((w, i) => (
                  <li key={i}><Text type="warning">{w}</Text></li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
